/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useState, useEffect, useRef } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import EditIcon from "@mui/icons-material/Edit";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PropTypes from "prop-types";
import "./AppDetailsLanguage.css";
import {
  Container,
  Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { fetchDeleteDescriptions } from "../../Services/redux/Application-details/ApplicationDetailsActions";
import ApplicationDetailsDescriptions from "../ApplicationDetails-Descriptions/ApplicationDetailsDescriptions";
import ApplicationDetailsServiceLinks from "../ApplicationDetailsServiceLinks/ApplicationDetailsServiceLinks";
import ApplicationDetailsScreenshots from "../ApplicationDetailsScreenshots/ApplicationDetailsScreenshots";
import ApplicationDetailsFeatureImage from "../ApplicationDetails-FeatureImage/ApplicationDetailsFeatureImage";
import { useSelector, useDispatch } from "react-redux";
import { fetchCurrentLanguage } from "../../Services/redux/languages-details/LanguageDetailsActions";
import { getConfig } from "../../config";
import AddLanguageDialog from "../ApplicationDetails-LanguageDialog/AddLanguageDialog";
import { fetchLanguageData } from '../../Services/redux/AddLanguageDialog/AddLanguageDialogActions';

function AppDetailsLanguage({
  languageData,
  submissionData,
  postCallTheNewSubmission,
  checkAllTabsAreTrue,
}) {
  const dispatch = useDispatch();
  const [tabs, setTabs] = useState([]);
  const [open, setOpen] = React.useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [currentLanguage, setCurrentLanguage] = useState("");
  const [makeDescriptions, setMakeDescriptions] = useState([]);
  const [showLanguage, setShowLanguage] = useState(["1 language is selected"]);
  const thirdPartyApps = globalThis.location.href.includes("third-party-apps");

  const [tabsKey, setTabsKey] = useState(0);
  const tabsRef = useRef(null);

  const handleScrollLeft = () => {
    const scroller = tabsRef.current?.querySelector('.MuiTabs-scroller');
    if (!scroller) return;
    const tabElements = Array.from(scroller.querySelectorAll('.MuiTab-root'));
    if (!tabElements.length) return;
    const scrollerRect = scroller.getBoundingClientRect();
    for (let i = tabElements.length - 1; i >= 0; i--) {
      const tabRect = tabElements[i].getBoundingClientRect();
      if (tabRect.left < scrollerRect.left - 2) {
        scroller.scrollTo({ left: scroller.scrollLeft + (tabRect.left - scrollerRect.left), behavior: 'smooth' });
        return;
      }
    }
    scroller.scrollTo({ left: 0, behavior: 'smooth' });
  };

  const handleScrollRight = () => {
    const scroller = tabsRef.current?.querySelector('.MuiTabs-scroller');
    if (!scroller) return;
    const tabElements = Array.from(scroller.querySelectorAll('.MuiTab-root'));
    if (!tabElements.length) return;
    const scrollerRect = scroller.getBoundingClientRect();
    for (const element of tabElements) {
      const tabRect = element.getBoundingClientRect();
      if (tabRect.right > scrollerRect.right) {
        scroller.scrollTo({ left: scroller.scrollLeft + (tabRect.left - scrollerRect.left), behavior: 'smooth' });
        return;
      }
    }
  };

  const appData = useSelector(
    (state) => state.applicationDetails.applicationDetails
  );

  useEffect(() => {
    if (
      (getConfig().REACT_APP_PORTAL === "seller" ||
        getConfig().REACT_APP_PORTAL === "domain-approver" ||
        getConfig().REACT_APP_PORTAL === "global-admin") &&
      submissionData?.descriptions?.length > 0
    ) {
      const languageSelectedOptions = [];
      const newTabs = languageData.filter((data) => {
        const found = submissionData.descriptions.some((e) => {
          if (
            getConfig().REACT_APP_PORTAL === "domain-approver" &&
            thirdPartyApps
          ) {
            const descLanguageFindIndex = e.language.replaceAll("_", "");
            const allLanguageFindIndex = data.languageTag.replaceAll("-", "");
            return descLanguageFindIndex === allLanguageFindIndex;
          } else {
            return e.language === data.languageTag;
          }
        });
        if (found) {
          data["default"] =
            getConfig().REACT_APP_PORTAL === "domain-approver" && thirdPartyApps
              ? submissionData.defaultLanguage.replaceAll("_", "") ===
              data.languageTag.replaceAll("-", "")
              : data.languageTag === submissionData.defaultLanguage;

          if (data.languageTag !== submissionData.defaultLanguage) {
            languageSelectedOptions.push(data.languageTag);
          }
          return true;
        }
        data["default"] = false;
        return false;
      });

      const fromIndex = newTabs.findIndex((e) => e.default);
      const element = newTabs.splice(fromIndex, 1);
      setSelectedOptions([...languageSelectedOptions]);
      newTabs.unshift(...element);

      setTabs([...newTabs]);
      setTabsKey(prev => prev + 1);
      setShowLanguage([`${newTabs.length} language${newTabs.length > 1 ? "s" : ""} selected`]);

      const currentLangExists = newTabs.some(tab => tab.languageTag === currentLanguage);
      const storedLang = sessionStorage.getItem("currentLanguage");

      if (!currentLangExists || !storedLang ||
        !newTabs.some(tab => tab.languageTag === storedLang)) {
        const defaultLang = submissionData.defaultLanguage;
        setCurrentLanguage(defaultLang);
        sessionStorage.setItem("currentLanguage", defaultLang);
        dispatch(fetchCurrentLanguage(defaultLang));

        newTabs.forEach((ele) => {
          ele.default = false;
          if (
            getConfig().REACT_APP_PORTAL === "domain-approver" &&
            thirdPartyApps
          ) {
            const descLanguageFindIndex = defaultLang.replaceAll("_", "");
            const allLanguageFindIndex = ele.languageTag.replaceAll("-", "");
            if (descLanguageFindIndex === allLanguageFindIndex) {
              ele.default = true;
            }
          } else if (ele.languageTag === defaultLang) {
              ele.default = true;
            }
          
        });
      } else {
        newTabs.forEach((ele) => {
          ele.default = ele.languageTag === storedLang;
        });
        setCurrentLanguage(storedLang);
        dispatch(fetchCurrentLanguage(storedLang));
      }

      setTabs([...newTabs]);

      let descriptions = [...submissionData.descriptions];
      descriptions = descriptions.map((ele) => {
        ele["added"] = true;
        return ele;
      });
      setMakeDescriptions([...descriptions]);
    } else if (
      getConfig().REACT_APP_PORTAL === "oem-admin" &&
      submissionData?.descriptions &&
      !Array.isArray(submissionData.descriptions)
    ) {
      const descriptionKeys = Object.keys(submissionData.descriptions);
      
      if (descriptionKeys.length > 0) {
        // Create tabs from the description keys
        const newTabs = descriptionKeys.map((key, index) => ({
          name: key,
          default: key === submissionData.defaultLang || index === 0,
          languageTag: key
        }));

        setTabs(newTabs);
        setTabsKey(prev => prev + 1);
        setShowLanguage([`${newTabs.length} language${newTabs.length > 1 ? "s" : ""} selected`]);

        // Set the default/first language as current
        const defaultLang = submissionData.defaultLang || descriptionKeys[0];
        setCurrentLanguage(defaultLang);
        sessionStorage.setItem("currentLanguage", defaultLang);
        dispatch(fetchCurrentLanguage(defaultLang));
      }
    }
  }, [languageData, submissionData, dispatch]);

  useEffect(() => {
    dispatch(fetchLanguageData());
  }, [dispatch]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setShowLanguage([`${tabs.length} languages is selected`]);
  };

  const handleResetSelection = () => {
    setSelectedOptions([]);
  };

  const updateManually = async () => {
    if (getConfig().REACT_APP_PORTAL !== "seller") return;

    let newDescriptions = [...makeDescriptions];
    let removeLanguages = [];

    newDescriptions = newDescriptions.filter((ele) => {
      if (ele.added) {
        delete ele["added"];
        return true;
      } else {
        if (ele.language !== submissionData.defaultLanguage) {
          removeLanguages.push(ele.language);
        }
        return false;
      }
    });

    let newSubmissionData = { ...submissionData };
    newSubmissionData["descriptions"] = [...newDescriptions];
    delete newSubmissionData["legalDocuments"];

    try {
      await postCallTheNewSubmission(newSubmissionData);
      await new Promise(resolve => setTimeout(resolve, 1500));

      if (removeLanguages.length > 0) {
        dispatch(
          fetchDeleteDescriptions(appData.id, newSubmissionData.id, {
            languages: removeLanguages,
          })
        );
      }

      await new Promise(resolve => setTimeout(resolve, 1000));

      const updatedMakeDescriptions = newDescriptions.map(desc => ({
        ...desc,
        added: true
      }));
      setMakeDescriptions(updatedMakeDescriptions);

      const newTabs = languageData.filter((data) => {
        const found = newDescriptions.some((e) => {
          if (getConfig().REACT_APP_PORTAL === "domain-approver" && thirdPartyApps) {
            const descLanguageFindIndex = e.language.replaceAll("_", "");
            const allLanguageFindIndex = data.languageTag.replaceAll("-", "");
            return descLanguageFindIndex === allLanguageFindIndex;
          } else {
            return e.language === data.languageTag;
          }
        });

        if (found) {
          data["default"] = data.languageTag === submissionData.defaultLanguage;
          return true;
        }
        return false;
      });

      const fromIndex = newTabs.findIndex((e) => e.default);
      if (fromIndex >= 0) {
        const element = newTabs.splice(fromIndex, 1);
        newTabs.unshift(...element);
      }

      const newSelectedOptions = newDescriptions
        .filter(desc => desc.language !== submissionData.defaultLanguage)
        .map(desc => desc.language);

      setSelectedOptions([...newSelectedOptions]);
      setTabs([...newTabs]);
      setTabsKey(prev => prev + 1);
      setShowLanguage([`${newTabs.length} language${newTabs.length > 1 ? "s" : ""} selected`]);

      const currentLangExists = newDescriptions.some(desc => desc.language === currentLanguage);
      if (!currentLangExists) {
        const defaultLang = submissionData.defaultLanguage;
        setCurrentLanguage(defaultLang);
        sessionStorage.setItem("currentLanguage", defaultLang);
        dispatch(fetchCurrentLanguage(defaultLang));
      }

      setOpen(false);

    } catch (error) {
      console.error("Error in updateManually:", error);
    }
  };

  const handleImportData = async (finalPayload, legalDocuments, finalLanguages) => {
    // Import data is only available for seller and seller-VW (IDP/OEM seller) users
    const isSeller = getConfig().REACT_APP_PORTAL === "seller";
    if (!isSeller) return;

    try {
      const defaultLanguage = submissionData.defaultLanguage;

      // Delete removed languages FIRST so the backend doesn't complain about
      // missing descriptions when we post the updated submission
      const currentLanguages = new Set(
        Array.isArray(submissionData.descriptions)
          ? submissionData.descriptions.map(d => d.language)
          : []
      );
      const languagesToRemove = [...currentLanguages].filter(lang =>
        lang !== defaultLanguage && !finalLanguages.includes(lang)
      );

      if (languagesToRemove.length > 0) {
        await fetchDeleteDescriptions(appData.id, submissionData.id, {
            languages: languagesToRemove,
          })(dispatch);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      // First API call - with descriptions and all submission data
      await postCallTheNewSubmission(finalPayload);
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Second API call - with legal documents merged into submission data so the backend
      // receives the required submission id alongside the legal documents payload
      if (legalDocuments?.legalDocumentsForAllLanguages && legalDocuments.legalDocumentsForAllLanguages.length > 0) {
        const legalDocsPayload = {
          ...finalPayload,
          ...legalDocuments,
        };
        delete legalDocsPayload.descriptions;
        await postCallTheNewSubmission(legalDocsPayload);
        await new Promise(resolve => setTimeout(resolve, 1500));
      }

      const updatedMakeDescriptions = finalPayload.descriptions.map(desc => ({
        ...desc,
        added: true
      }));
      setMakeDescriptions(updatedMakeDescriptions);

      // Deduplicate finalLanguages — handles both regular seller and seller-VW (IDP/OEM seller)
      const uniqueFinalLanguages = [...new Set(finalLanguages)];

      const newTabs = languageData.filter((data) => {
        return uniqueFinalLanguages.includes(data.languageTag);
      }).map((data) => ({
        ...data,
        default: data.languageTag === defaultLanguage,
      }));

      const defaultIndex = newTabs.findIndex(tab => tab.default);
      if (defaultIndex > 0) {
        const [defaultTab] = newTabs.splice(defaultIndex, 1);
        newTabs.unshift(defaultTab);
      }

      const newSelectedOptions = uniqueFinalLanguages.filter(lang => lang !== defaultLanguage);
      const totalCount = uniqueFinalLanguages.length;

      setTabs([...newTabs]);
      setTabsKey(prev => prev + 1);
      setSelectedOptions([...newSelectedOptions]);
      setShowLanguage([`${totalCount} language${totalCount > 1 ? "s" : ""} selected`]);

      if (submissionData) {
        submissionData.descriptions = [...finalPayload.descriptions];
      }

      const currentLangExists = uniqueFinalLanguages.includes(currentLanguage);

      if (!currentLangExists) {
        setCurrentLanguage(defaultLanguage);
        sessionStorage.setItem("currentLanguage", defaultLanguage);
        dispatch(fetchCurrentLanguage(defaultLanguage));
      }

      setOpen(false);

    } catch (error) {
      console.error("Error in handleImportData:", error);
    }
  };

  const changeTab = (index, langTag) => {
    const newTabs = [...tabs];
    newTabs.forEach((ele) => {
      if (ele.default) {
        ele.default = false;
      }
    });
    newTabs[index].default = true;
    setTabs([...newTabs]);
    setCurrentLanguage(langTag);
    sessionStorage.setItem("currentLanguage", langTag);
    dispatch(fetchCurrentLanguage(langTag));
  };

  const handleCheckBox = ($event, languageTag) => {
    let newSelectedOptions = [...selectedOptions];
    let description = [...makeDescriptions];
    if (
      $event.target.checked &&
      !makeDescriptions.some((ele) => ele.language === languageTag)
    ) {
      newSelectedOptions.push(languageTag);
      let obj = {};
      obj["banner"] = null;
      obj["language"] = languageTag;
      obj["screenshots"] = [];
      obj["added"] = true;
      obj["applicationName"] = submissionData.descriptions[0].applicationName;
      description.push(obj);
    } else if (
      !$event.target.checked &&
      makeDescriptions.findIndex((ele) => ele.language === languageTag) > 0
    ) {
      newSelectedOptions = newSelectedOptions.filter(
        (item) => item !== languageTag
      );
      description = description.map((ele) => {
        if (ele.language === languageTag) {
          ele["added"] = false;
        }
        return ele;
      });
    } else if (
      $event.target.checked &&
      makeDescriptions.some((ele) => ele.language === languageTag)
    ) {
      newSelectedOptions.push(languageTag);
      description = description.map((ele) => {
        if (ele.language === languageTag) {
          ele["added"] = true;
        }
        return ele;
      });
    }
    const totalCount = 1 + newSelectedOptions.length;
    setShowLanguage([`${totalCount} language${totalCount > 1 ? "s" : ""} selected`]);
    setMakeDescriptions([...description]);
    setSelectedOptions([...newSelectedOptions]);
  };

  const handleLanguageSelectionChange = (newSelectedLanguages) => {
    let description = [...makeDescriptions];
    const currentLanguages = new Set(description.map(d => d.language));

    newSelectedLanguages.forEach(langTag => {
      if (langTag !== submissionData.defaultLanguage && !currentLanguages.has(langTag)) {
        let obj = {};
        obj["banner"] = null;
        obj["language"] = langTag;
        obj["screenshots"] = [];
        obj["added"] = true;
        obj["applicationName"] = submissionData.descriptions[0]?.applicationName || "";
        description.push(obj);
      }
    });

    description = description.map((ele) => {
      if (ele.language !== submissionData.defaultLanguage) {
        if (newSelectedLanguages.includes(ele.language)) {
          ele["added"] = true;
        } else {
          ele["added"] = false;
        }
      }
      return ele;
    });

    const totalCount = 1 + newSelectedLanguages.length;
    setShowLanguage([`${totalCount} language${totalCount > 1 ? "s" : ""} selected`]);
    setMakeDescriptions([...description]);
    setSelectedOptions([...newSelectedLanguages]);

    const newTabs = languageData.filter((data) => {
      const isDefaultLang = data.languageTag === submissionData.defaultLanguage;
      const isSelectedLang = newSelectedLanguages.includes(data.languageTag);

      if (isDefaultLang || isSelectedLang) {
        data["default"] = isDefaultLang;
        return true;
      }
      return false;
    });

    const defaultIndex = newTabs.findIndex(tab => tab.default);
    if (defaultIndex > 0) {
      const [defaultTab] = newTabs.splice(defaultIndex, 1);
      newTabs.unshift(defaultTab);
    }

    setTabs([...newTabs]);
    setTabsKey(prev => prev + 1);
  };

  const renderPortalTabs = () => {
    const isSellerOrApproverOrAdmin =
      getConfig().REACT_APP_PORTAL === "seller" ||
      getConfig().REACT_APP_PORTAL === "domain-approver" ||
      getConfig().REACT_APP_PORTAL === "global-admin";

    if (isSellerOrApproverOrAdmin) {
      return (
        <Tabs ref={tabsRef} key={tabsKey} variant="scrollable" scrollButtons={false}>
          {tabs.map((tabData, index) => {
            if (index === 0) {
              return (
                <Tab
                  key={`${tabData.languageTag}-${index}-default`}
                  className={tabData.default ? "default-name" : "tab-names"}
                  label={`${tabData.defaultName} (Default)`}
                  onClick={() => changeTab(index, tabData.languageTag)}
                ></Tab>
              );
            }
            return (
              <Tab
                key={`${tabData.languageTag}-${index}`}
                className={tabData.default ? "default-name" : "tab-names"}
                label={
                  tabData.defaultCountry
                    ? `${tabData.defaultName} (${tabData.defaultCountry})`
                    : `${tabData.defaultName}`
                }
                onClick={() => changeTab(index, tabData.languageTag)}
              ></Tab>
            );
          })}
        </Tabs>
      );
    }

    return (
      <Tabs ref={tabsRef} key={tabsKey} variant="scrollable" scrollButtons={false}>
        {tabs.map((tabData, index) => (
          <Tab
            key={`${tabData.name}-${index}`}
            className={tabData.default ? "default-name" : "tab-names"}
            label={
              tabData.name === submissionData.defaultLang
                ? `${tabData.name} (Default)`
                : `${tabData.name}`
            }
            onClick={() => changeTab(index, tabData.name)}
          />
        ))}
      </Tabs>
    );
  };

  return (
    <Container maxWidth="lg">
      <Grid2 container justifyContent="flex-start" alignItems="center">
        <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
          <div
            id="description-servicelink"
            style={
              submissionData.status === "DRAFT"
                ? { marginTop: "30px", width: "85%" }
                : { marginTop: "30px" }
            }
            className={
              submissionData.status === "DRAFT"
                ? "description-language-draft"
                : "description"
            }
          >
            {submissionData.status === "DRAFT" && (
              <div
                style={{
                  height: "4px",
                  width: "100%",
                  margin: "24px 0px",
                  border: "1px solid #12141533",
                  borderLeft: "none",
                  borderRight: "none",
                }}
              ></div>
            )}
            <Typography
              sx={{
                "font-weight": "bold !important",
                "font-size": "20px !important",
                color: "rgba(18, 20, 21, 1) !important",
              }}
              className="uploadSignedApkText"
            >
              Descriptions
            </Typography>
            <div className="enclosing-div">
              <div className="tabs-div" style={{ marginTop: "0px" }}>
                <stack direction="row" spacing={2}>
                  <div className="tabs">
                    {tabs && tabs.length > 0
                      ? renderPortalTabs()
                      : <div style={{ padding: '10px' }}>Loading languages...</div>
                    }
                  </div>
                </stack>
              </div>
              <div className="tabs-right-actions">
                <div className="tab-scroll-buttons">
                  <button className="tab-scroll-btn" onClick={handleScrollLeft} aria-label="scroll left">
                    <ChevronLeftIcon sx={{ fontSize: "16px" }} />
                  </button>
                  <button className="tab-scroll-btn" onClick={handleScrollRight} aria-label="scroll right">
                    <ChevronRightIcon sx={{ fontSize: "16px" }} />
                  </button>
                </div>
                {submissionData &&
                  Object.keys(submissionData).length > 0 &&
                  submissionData.status === "DRAFT" &&
                  getConfig().REACT_APP_PORTAL === "seller" && (
                    <div className="add-language-button">
                      <button className="add-language" onClick={handleClickOpen}>
                        <EditIcon
                          className="edit-icon-language"
                          sx={{ fontSize: "16px" }}
                        />
                        Add Language
                      </button>
                    </div>
                  )}
              </div>
            </div>
            <div className="description-div" style={{ width: "100%" }}>
              {currentLanguage && currentLanguage.length > 0 && (
                <ApplicationDetailsDescriptions
                  style={{ color: "grey" }}
                  postCallTheNewSubmission={postCallTheNewSubmission}
                />
              )}
            </div>
            <div
              className={
                submissionData.status == "DRAFT"
                  ? "description-div service-links-language"
                  : "service-links-notdraf"
              }
              style={{ width: "100%" }}
            >
              {getConfig().REACT_APP_PORTAL === "seller" &&
                (sessionStorage.getItem("idpLogin") !== "true" ||
                  sessionStorage.getItem("isOemSeller")) && (
                  <>
                    <p className="description-text">Service Links</p>
                    {currentLanguage && currentLanguage.length > 0 && (
                      <ApplicationDetailsServiceLinks
                        style={{ color: "grey" }}
                        postCallTheNewSubmission={postCallTheNewSubmission}
                      />
                    )}
                  </>
                )}
              {getConfig().REACT_APP_PORTAL === "oem-admin" &&
                (!submissionData.isOemApp ||
                  sessionStorage.getItem("idpLogin") !== "true") && (
                  <>
                    <p className="description-text">Service Links</p>
                    {currentLanguage && currentLanguage.length > 0 && (
                      <ApplicationDetailsServiceLinks
                        style={{ color: "grey" }}
                        postCallTheNewSubmission={postCallTheNewSubmission}
                      />
                    )}
                  </>
                )}
              {getConfig().REACT_APP_PORTAL === "domain-approver" ||
                (getConfig().REACT_APP_PORTAL === "global-admin" &&
                  !thirdPartyApps &&
                  sessionStorage.getItem("idpLogin") !== "true" && (
                    <>
                      <p className="description-text">Service Links</p>
                      {currentLanguage && currentLanguage.length > 0 && (
                        <ApplicationDetailsServiceLinks
                          style={{ color: "grey" }}
                          postCallTheNewSubmission={postCallTheNewSubmission}
                        />
                      )}
                    </>
                  ))}
            </div>
            <div className="description-div" style={{ width: "100%" }}>
              <p className="description-text">
                Screenshots ({currentLanguage}){" "}
                <span style={{ color: "red" }}>*</span>
              </p>
              {currentLanguage && currentLanguage.length > 0 && (
                <ApplicationDetailsScreenshots
                  style={{ color: "grey" }}
                  postCallTheNewSubmission={postCallTheNewSubmission}
                  checkAllTabsAreTrue={checkAllTabsAreTrue}
                />
              )}
            </div>

            <div className="description-div" style={{ width: "100%" }}>
              <p className="description-text-feature-image">
                Feature Image ({currentLanguage})
              </p>
              {currentLanguage && currentLanguage.length > 0 && (
                <ApplicationDetailsFeatureImage
                  currentLanguage={currentLanguage}
                />
              )}
            </div>
            {submissionData.status === "DRAFT" && (
              <div
                style={{
                  height: "4px",
                  width: "100%",
                  margin: "24px 0px",
                  borderTop: "1px solid #12141533",
                  borderBottom: "1px solid #12141533",
                  borderLeft: "none",
                  borderRight: "none",
                }}
              ></div>
            )}
            {/* )} */}
            {submissionData?.status === "DRAFT" &&
              getConfig().REACT_APP_PORTAL === "seller" && (
              <AddLanguageDialog
                open={open}
                onClose={handleClose}
                languageData={languageData}
                submissionData={submissionData}
                selectedOptions={selectedOptions}
                showLanguage={showLanguage}
                onLanguageChange={handleLanguageSelectionChange}
                onCheckBoxChange={handleCheckBox}
                onUpdateManually={updateManually}
                onResetSelection={handleResetSelection}
                onImportData={handleImportData}
              />
            )}
          </div>
        </Grid2>
      </Grid2>
    </Container>
  );
}

AppDetailsLanguage.propTypes = {
  languageData: PropTypes.array.isRequired,
  submissionData: PropTypes.object.isRequired,
  postCallTheNewSubmission: PropTypes.func.isRequired,
  checkAllTabsAreTrue: PropTypes.func.isRequired,
};

export default AppDetailsLanguage;
